// src/supplier/pages/SupplierRegister.jsx
import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { supplierAuthAPI } from "../services/supplierApi";
import { Building2, Loader2, CheckCircle } from "lucide-react";
import PasswordInput from "../../components/PasswordInput";
import ThemeToggle from "../../components/ThemeToggle";

export default function SupplierRegister() {
  const [params] = useSearchParams();
  const invitationToken = params.get("token") || "";

  const [form, setForm] = useState({
    companyName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    invitationToken,
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setFieldErrors((fe) => ({ ...fe, [field]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.companyName.trim()) errs.companyName = "Company name is required.";
    if (!form.email.trim()) errs.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email address.";
    if (!form.password) errs.password = "Password is required.";
    else if (form.password.length < 8) errs.password = "Minimum 8 characters.";
    if (form.password !== form.confirmPassword) errs.confirmPassword = "Passwords do not match.";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    try {
      const res = await supplierAuthAPI.register({
        companyName: form.companyName.trim(),
        email: form.email.trim(),
        password: form.password,
        phone: form.phone.trim() || undefined,
        invitationToken: form.invitationToken || undefined,
      });

      // Redirect to OTP verification page
      const verification = res?.verification || {};
      try { sessionStorage.setItem("supplier_pending_verification_email", form.email.trim().toLowerCase()); } catch {}
      navigate("/supplier/verify-email", {
        state: {
          email: verification.email || form.email.trim(),
          verification,
          deliveryStatus: res?.delivery?.status,
          cooldownSeconds: verification.resendCooldownSeconds || 0,
        },
      });
    } catch (err) {
      setError(err?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="supplier-portal relative flex min-h-screen items-center justify-center overflow-hidden bg-[#f5f5f4] px-4 py-12 dark:bg-[#0a0a0a] sm:py-16">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div
          className="absolute inset-0 opacity-70 dark:hidden"
          style={{
            backgroundImage:
              "linear-gradient(rgba(23, 23, 23, 0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(23, 23, 23, 0.045) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div
          className="absolute inset-0 hidden opacity-80 dark:block"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        <div className="absolute left-1/2 top-1/2 h-[32rem] w-[32rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/80 blur-3xl dark:bg-white/[0.055] sm:h-[42rem] sm:w-[42rem]" />

        <svg
          className="absolute -right-12 top-4 h-[18rem] w-[21rem] text-neutral-900/[0.055] dark:text-white/[0.06] sm:right-8 sm:top-8 sm:h-[24rem] sm:w-[28rem]"
          viewBox="0 0 560 470"
          fill="none"
        >
          <path d="M68 124L178 66L270 138L382 58L486 132" stroke="currentColor" strokeWidth="1.35" />
          <path d="M178 66L150 226L270 138L326 252L486 132" stroke="currentColor" strokeWidth="1.15" />
          <path d="M150 226L92 346L236 392L326 252L452 348L510 246L486 132" stroke="currentColor" strokeWidth="1.35" />
          <path d="M68 124L150 226M270 138L236 392M326 252L510 246" stroke="currentColor" strokeWidth="0.9" strokeDasharray="5 9" />
          <circle cx="68" cy="124" r="5" fill="currentColor" />
          <circle cx="178" cy="66" r="7" fill="currentColor" />
          <circle cx="270" cy="138" r="4" fill="currentColor" />
          <circle cx="382" cy="58" r="5" fill="currentColor" />
          <circle cx="486" cy="132" r="7" fill="currentColor" />
          <circle cx="150" cy="226" r="4" fill="currentColor" />
          <circle cx="326" cy="252" r="8" fill="currentColor" />
          <circle cx="92" cy="346" r="5" fill="currentColor" />
          <circle cx="236" cy="392" r="7" fill="currentColor" />
          <circle cx="452" cy="348" r="4" fill="currentColor" />
          <circle cx="510" cy="246" r="5" fill="currentColor" />
        </svg>

        <svg
          className="absolute -bottom-10 -left-16 h-[17rem] w-[23rem] text-neutral-900/[0.045] dark:text-white/[0.05] sm:bottom-0 sm:left-5 sm:h-[21rem] sm:w-[28rem]"
          viewBox="0 0 620 440"
          fill="none"
        >
          <path d="M30 354C122 354 138 230 226 230C314 230 326 96 426 96C506 96 530 166 596 166" stroke="currentColor" strokeWidth="1.5" />
          <path d="M30 390C140 390 158 278 248 278C350 278 350 142 446 142C516 142 548 210 604 210" stroke="currentColor" strokeWidth="1" strokeDasharray="7 10" />
          <path d="M226 230L248 278M426 96L446 142" stroke="currentColor" strokeWidth="1" />
          <circle cx="30" cy="354" r="5" fill="currentColor" />
          <circle cx="226" cy="230" r="7" fill="currentColor" />
          <circle cx="426" cy="96" r="6" fill="currentColor" />
          <circle cx="596" cy="166" r="5" fill="currentColor" />
          <circle cx="248" cy="278" r="4" fill="currentColor" />
          <circle cx="446" cy="142" r="4" fill="currentColor" />
          <circle cx="604" cy="210" r="4" fill="currentColor" />
        </svg>

        <div className="absolute bottom-8 left-8 hidden items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.35em] text-neutral-900/35 dark:text-white/30 sm:flex">
          <span className="h-px w-10 bg-current" />
          Be a Supply Partner
        </div>
      </div>

      <ThemeToggle
        variant="icon"
        className="fixed bottom-5 right-5 z-50 sm:bottom-6 sm:right-6"
      />

      <div className="relative z-10 w-full max-w-[488px] rounded-2xl border border-white/80 bg-white/90 p-6 shadow-[0_24px_70px_-28px_rgba(0,0,0,0.35)] backdrop-blur-xl dark:border-white/10 dark:bg-neutral-900/85 dark:shadow-[0_24px_70px_-28px_rgba(0,0,0,0.9)]">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/50">
            <Building2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">Create Supplier Account</h1>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Register to access your procurement portal</p>
        </div>

        {invitationToken && (
          <div className="mb-4 flex items-start gap-2 rounded-lg bg-blue-50 p-3 dark:bg-blue-950/30">
            <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" />
            <p className="text-xs text-blue-700 dark:text-blue-300">
              You were invited by a business. Your account will be automatically linked after registration.
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 p-3 text-xs text-red-700 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Company Name — the supplier entity identity */}
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Company Name <span className="text-red-400">*</span></label>
            <input
              type="text" autoFocus
              value={form.companyName} onChange={set("companyName")}
              className={`w-full rounded-xl border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-400 dark:bg-neutral-800 dark:text-neutral-100 ${fieldErrors.companyName ? "border-red-400 dark:border-red-500" : "border-neutral-200 dark:border-neutral-700"}`}
              placeholder="e.g. ABC Textile Co."
            />
            {fieldErrors.companyName && <p className="mt-0.5 text-xs text-red-500">{fieldErrors.companyName}</p>}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Email <span className="text-red-400">*</span></label>
            <input
              type="email"
              value={form.email} onChange={set("email")}
              className={`w-full rounded-xl border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-400 dark:bg-neutral-800 dark:text-neutral-100 ${fieldErrors.email ? "border-red-400 dark:border-red-500" : "border-neutral-200 dark:border-neutral-700"}`}
              placeholder="supplier@example.com"
            />
            {fieldErrors.email && <p className="mt-0.5 text-xs text-red-500">{fieldErrors.email}</p>}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Phone <span className="text-neutral-400">(optional)</span></label>
            <input
              type="tel"
              value={form.phone} onChange={set("phone")}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
              placeholder="+63 912 345 6789"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Password <span className="text-red-400">*</span></label>
            <PasswordInput
              value={form.password} onChange={set("password")}
              className={`w-full rounded-xl border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-400 dark:bg-neutral-800 dark:text-neutral-100 ${fieldErrors.password ? "border-red-400 dark:border-red-500" : "border-neutral-200 dark:border-neutral-700"}`}
              placeholder="Minimum 8 characters"
            />
            {fieldErrors.password && <p className="mt-0.5 text-xs text-red-500">{fieldErrors.password}</p>}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Confirm Password <span className="text-red-400">*</span></label>
            <PasswordInput
              value={form.confirmPassword} onChange={set("confirmPassword")}
              className={`w-full rounded-xl border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-400 dark:bg-neutral-800 dark:text-neutral-100 ${fieldErrors.confirmPassword ? "border-red-400 dark:border-red-500" : "border-neutral-200 dark:border-neutral-700"}`}
              placeholder="••••••••"
            />
            {fieldErrors.confirmPassword && <p className="mt-0.5 text-xs text-red-500">{fieldErrors.confirmPassword}</p>}
          </div>

          {invitationToken && (
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Invitation Token</label>
              <input
                type="text" readOnly
                value={form.invitationToken}
                className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-xs font-mono text-neutral-500 outline-none dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-400"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition disabled:opacity-50"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Creating account…" : "Create Account"}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-neutral-500 dark:text-neutral-400">
          Already have an account?{" "}
          <Link to="/supplier/login" className="text-blue-600 hover:underline dark:text-blue-400">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
